import React, { useCallback, useState, useRef, useEffect } from "react";
import { Container, Graphics, Text } from "@pixi/react";
import { CornerNode, SideNode, CenterNode } from "../nodes";
import EditableLine from "./EditableLine";
import "pixi.js/events";
import { useApp } from "../../context/AppContext";

const Rectangle = ({ 
  shape, 
  isSelected, 
  onUpdate, 
  onSelect, 
  stageRef, 
  isPartOfMergedStructure = false,
  onShapePointerDown = null,
  isMergedStructureShape = false,
  mergedStructureId = null,
  selectedMeasurementUnit = {
    id: 'meters',
    name: 'Meters',
    symbol: 'm',
    abbreviation: 'm',
    conversionFactor: 1,
  },
  isBlueprintMode = false
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isScaling, setIsScaling] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [initialShape, setInitialShape] = useState(null);
  const [activeCorner, setActiveCorner] = useState(null);
  const [selectedLineIndex, setSelectedLineIndex] = useState(null);
  const [isModifierMode, setIsModifierMode] = useState(false);
  const [rotatingLineIndex, setRotatingLineIndex] = useState(null);
  const [rotationStart, setRotationStart] = useState(null);
  // Track which sides are split (default: all false - single line per side)
  const [splitSides, setSplitSides] = useState(shape.splitSides || { top: false, right: false, bottom: false, left: false });
  const containerRef = useRef();
  const isNodeClicked = useRef(false);
  const isDeletingLine = useRef(false); // Track deletion in progress
  
  const { stage, canvas } = useApp();

  // Helper function to convert pixels to selected unit
  const convertToSelectedUnit = useCallback((pixelValue) => {
    // Convert from pixels to meters (assuming 100 pixels = 1 meter)
    const meters = pixelValue / 100;
    // Convert from meters to selected unit
    const convertedValue = meters * selectedMeasurementUnit.conversionFactor;
    return convertedValue;
  }, [selectedMeasurementUnit]);

  // Helper function to format dimension text with unit
  const formatDimension = useCallback((pixelValue) => {
    const convertedValue = convertToSelectedUnit(pixelValue);
    const formattedValue = convertedValue.toFixed(1);
    return `${formattedValue}${selectedMeasurementUnit.abbreviation}`;
  }, [convertToSelectedUnit, selectedMeasurementUnit]);

  const getCoordinates = useCallback(
    (clientX, clientY) => {
    const stage = stageRef.current;
    if (!stage) return null;

    const canvas = stage._canvas;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const scaleX = stage.app ? stage.app.screen.width / rect.width : 1;
    const scaleY = stage.app ? stage.app.screen.height / rect.height : 1;

    return {
      x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY,
    };
    },
    [stageRef]
  );

  const handleMouseDown = useCallback(
    (event) => {
      if (!shape) return;
      
      event.stopPropagation();
      onSelect();

      // If this rectangle is part of a merged structure and has a custom drag handler
      if (onShapePointerDown) {
        onShapePointerDown(event);
        return;
      }

      // Skip individual rectangle dragging if part of merged structure
      // But still allow individual line interaction (curving, etc.)
      if (isPartOfMergedStructure) return;

      // Get coordinates properly
      let globalPoint;
      if (event.data?.global) {
        globalPoint = event.data.global;
      } else if (event.clientX !== undefined) {
        globalPoint = getCoordinates(event.clientX, event.clientY);
        if (!globalPoint) return;
      } else {
        return; // Invalid event
      }

      setIsDragging(true);
      setDragStart({
        x: globalPoint.x - shape.x,
        y: globalPoint.y - shape.y,
      });
      
      // Notify merge system of drag start
      window.dispatchEvent(
        new CustomEvent("shapeDragStart", {
          detail: { shapeId: shape.id },
        })
      );

      window.dispatchEvent(
        new CustomEvent("propogation-block", { detail: { isBlocked: false } })
      );
    },
    [
      shape,
      onSelect,
      getCoordinates,
      isPartOfMergedStructure,
      onShapePointerDown,
    ]
  );

  const handleMouseMove = useCallback(
    (event) => {
      if (!isDragging || !dragStart) return;

      // Get coordinates properly
      let globalPoint;
      if (event.data?.global) {
        globalPoint = event.data.global;
      } else if (event.clientX !== undefined) {
        globalPoint = getCoordinates(event.clientX, event.clientY);
        if (!globalPoint) return;
      } else {
        return; // Invalid event
      }

      const newX = globalPoint.x - dragStart.x;
      const newY = globalPoint.y - dragStart.y;

      onUpdate({ id: shape.id, x: newX, y: newY });
      
      // Notify merge system of drag update
      window.dispatchEvent(
        new CustomEvent("shapeDragUpdate", {
        detail: { 
          shapeId: shape.id, 
          position: { x: newX, y: newY },
            boundingBox: {
              x: newX,
              y: newY,
              width: shape.width,
              height: shape.height,
            },
          },
        })
      );
    },
    [isDragging, dragStart, onUpdate, getCoordinates, shape]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
    
    // Notify merge system of drag end
    if (shape) {
      window.dispatchEvent(
        new CustomEvent("shapeDragEnd", {
          detail: { shapeId: shape.id },
        })
      );
    }
  }, [shape]);

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (event) => {
        if (!dragStart) return;

        const globalPoint = getCoordinates(event.clientX, event.clientY);
        if (!globalPoint) return;

        const newX = globalPoint.x - dragStart.x;
        const newY = globalPoint.y - dragStart.y;

        onUpdate({ id: shape.id, x: newX, y: newY });
      };

      const handleGlobalMouseUp = () => {
        setIsDragging(false);
        setDragStart(null);
      };

      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }
  }, [isDragging, dragStart, onUpdate, getCoordinates]);

  // Add global mouse event listeners for scaling

  const handleNodeMouseMove = useCallback(
    (event, globalPoint) => {
      if (!isScaling || !dragStart || !initialShape || activeCorner === null)
        return;

      // Calculate deltas from the corner's original position to the new mouse position
      const deltaX = globalPoint.x - dragStart.x;
      const deltaY = globalPoint.y - dragStart.y;

      let updates = {};
      switch (activeCorner) {
        case 0: // top-left
          updates = {
            width: Math.max(20, initialShape.width - deltaX),
            height: Math.max(20, initialShape.height - deltaY),
            x: globalPoint.x,
            y: globalPoint.y,
          };
          break;
        case 1: // top-right
          updates = {
            width: Math.max(20, globalPoint.x - initialShape.x),
            height: Math.max(20, initialShape.height - deltaY),
            y: globalPoint.y,
          };
          break;
        case 2: // bottom-right
          updates = {
            width: Math.max(20, globalPoint.x - initialShape.x),
            height: Math.max(20, globalPoint.y - initialShape.y),
          };
          break;
        case 3: // bottom-left
          updates = {
            width: Math.max(20, initialShape.width - deltaX),
            height: Math.max(20, globalPoint.y - initialShape.y),
            x: globalPoint.x,
          };
          break;
      }

      // Ensure updates maintain aspect ratio if shift is held
      if (event.shiftKey) {
        const ratio = initialShape.width / initialShape.height;
        if (updates.width && updates.height) {
          if (Math.abs(deltaX) > Math.abs(deltaY)) {
            updates.height = updates.width / ratio;
            // Adjust y position for top corners when maintaining aspect ratio
            if (activeCorner === 0 || activeCorner === 1) {
              updates.y = initialShape.y + initialShape.height - updates.height;
            }
          } else {
            updates.width = updates.height * ratio;
            // Adjust x position for left corners when maintaining aspect ratio
            if (activeCorner === 0 || activeCorner === 3) {
              updates.x = initialShape.x + initialShape.width - updates.width;
            }
          }
        }
      }

      // Ensure minimum size
      updates.width = Math.max(20, updates.width);
      updates.height = Math.max(20, updates.height);

      onUpdate({ id: shape.id, ...updates });

      // Notify merge system of scaling operation
      const scaledShape = {
        ...shape,
        ...updates,
      };
      
      window.dispatchEvent(
        new CustomEvent("shapeScaleUpdate", {
        detail: { 
          shapeId: shape.id, 
          scaledShape: scaledShape,
          boundingBox: { 
            x: updates.x !== undefined ? updates.x : shape.x, 
            y: updates.y !== undefined ? updates.y : shape.y, 
            width: updates.width, 
              height: updates.height,
            },
            isScaling: true,
          },
        })
      );
    },
    [isScaling, dragStart, initialShape, activeCorner, onUpdate, shape]
  );

  useEffect(() => {
    if (isScaling) {
      const handleGlobalMouseMove = (event) => {
        const globalPoint = getCoordinates(event.clientX, event.clientY);
        if (!globalPoint) return;
        
        handleNodeMouseMove(event, globalPoint);
      };

      const handleGlobalMouseUp = () => {
        // Notify merge system of scaling end if we were scaling
        if (isScaling && shape) {
          window.dispatchEvent(
            new CustomEvent("shapeScaleEnd", {
              detail: { shapeId: shape.id },
            })
          );
        }
        
        setIsScaling(false);
        setActiveCorner(null);
        setDragStart(null);
        setInitialShape(null);
      };

      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }
  }, [isScaling, handleNodeMouseMove, getCoordinates]);

  const handleNodeMouseDown = useCallback(
    (event, nodeType, index, globalPoint) => {
      if (!shape) return;

      event.stopPropagation();
      window.dispatchEvent(
        new CustomEvent("propogation-block", { detail: { isBlocked: true } })
      );
    
      if (nodeType === "corner") {
        setIsScaling(true);
        setActiveCorner(index);
        setInitialShape({ ...shape });
        // Store the corner's initial position instead of mouse position
        const cornerPos = {
          x: shape.x + (index === 1 || index === 2 ? shape.width : 0),
          y: shape.y + (index === 2 || index === 3 ? shape.height : 0),
        };
        setDragStart(cornerPos);

        // Notify merge system of scaling start
        window.dispatchEvent(
          new CustomEvent("shapeScaleStart", {
            detail: { shapeId: shape.id, scaledShape: shape },
          })
        );
      }
    },
    [shape]
  );

  // Simplified node mouse up since global handlers will take care of cleanup
  const handleNodeMouseUp = useCallback(() => {
    // Notify merge system of scaling end
    if (isScaling && shape) {
      window.dispatchEvent(
        new CustomEvent("shapeScaleEnd", {
          detail: { shapeId: shape.id },
        })
      );
    }
  }, [isScaling, shape]);

  // Generate line shapes for each side of the rectangle
  const generateRectangleLines = useCallback(() => {
    if (!shape) return [];

    // Get line scaling data (extend if needed for backward compatibility)
    const lineScaling = shape.lineScaling || new Array(8).fill(0);
    const lineData = shape.lineData || new Array(8).fill({});

    // Define the base rectangle corners
    const corners = [
      { x: shape.x, y: shape.y }, // top-left
      { x: shape.x + shape.width, y: shape.y }, // top-right
      { x: shape.x + shape.width, y: shape.y + shape.height }, // bottom-right
      { x: shape.x, y: shape.y + shape.height }, // bottom-left
    ];

    // Generate segments based on split state
    const baseSegments = [];
    let currentIndex = 0;

    // Top side
    if (splitSides.top) {
      // Split into 2 segments
      const baseHalfWidth = shape.width / 2;
      const topSegment1Length = baseHalfWidth + (lineScaling[0] || 0);
      const topSegment2Length = baseHalfWidth + (lineScaling[1] || 0);
      
      baseSegments.push({
        start: corners[0],
        end: { x: shape.x + topSegment1Length, y: shape.y },
        originalIndex: currentIndex++,
        side: 'top',
        segment: 1
      });
      baseSegments.push({
        start: { x: shape.x + topSegment1Length, y: shape.y },
        end: corners[1],
        originalIndex: currentIndex++,
        side: 'top',
        segment: 2
      });
    } else {
      // Single segment
      baseSegments.push({
        start: corners[0],
        end: corners[1],
        originalIndex: currentIndex++,
        side: 'top',
        segment: 1
      });
    }

    // Right side
    if (splitSides.right) {
      // Split into 2 segments
      const baseHalfHeight = shape.height / 2;
      const rightSegment1Length = baseHalfHeight + (lineScaling[2] || 0);
      const rightSegment2Length = baseHalfHeight + (lineScaling[3] || 0);
      
      baseSegments.push({
        start: corners[1],
        end: { x: shape.x + shape.width, y: shape.y + rightSegment1Length },
        originalIndex: currentIndex++,
        side: 'right',
        segment: 1
      });
      baseSegments.push({
        start: { x: shape.x + shape.width, y: shape.y + rightSegment1Length },
        end: corners[2],
        originalIndex: currentIndex++,
        side: 'right',
        segment: 2
      });
    } else {
      // Single segment
      baseSegments.push({
        start: corners[1],
        end: corners[2],
        originalIndex: currentIndex++,
        side: 'right',
        segment: 1
      });
    }

    // Bottom side
    if (splitSides.bottom) {
      // Split into 2 segments
      const baseHalfWidth = shape.width / 2;
      const bottomSegment1Length = baseHalfWidth + (lineScaling[4] || 0);
      const bottomSegment2Length = baseHalfWidth + (lineScaling[5] || 0);
      
      baseSegments.push({
        start: corners[2],
        end: { x: shape.x + shape.width - bottomSegment1Length, y: shape.y + shape.height },
        originalIndex: currentIndex++,
        side: 'bottom',
        segment: 1
      });
      baseSegments.push({
        start: { x: shape.x + shape.width - bottomSegment1Length, y: shape.y + shape.height },
        end: corners[3],
        originalIndex: currentIndex++,
        side: 'bottom',
        segment: 2
      });
    } else {
      // Single segment
      baseSegments.push({
        start: corners[2],
        end: corners[3],
        originalIndex: currentIndex++,
        side: 'bottom',
        segment: 1
      });
    }

    // Left side
    if (splitSides.left) {
      // Split into 2 segments
      const baseHalfHeight = shape.height / 2;
      const leftSegment1Length = baseHalfHeight + (lineScaling[6] || 0);
      const leftSegment2Length = baseHalfHeight + (lineScaling[7] || 0);
      
      baseSegments.push({
        start: corners[3],
        end: { x: shape.x, y: shape.y + shape.height - leftSegment1Length },
        originalIndex: currentIndex++,
        side: 'left',
        segment: 1
      });
      baseSegments.push({
        start: { x: shape.x, y: shape.y + shape.height - leftSegment1Length },
        end: corners[0],
        originalIndex: currentIndex++,
        side: 'left',
        segment: 2
      });
    } else {
      // Single segment
      baseSegments.push({
        start: corners[3],
        end: corners[0],
        originalIndex: currentIndex++,
        side: 'left',
        segment: 1
      });
    }

    // Apply rotations to individual segments and maintain rectangle closure
    const rotatedSegments = [];
    
    // First pass: calculate all rotated positions
    const calculatedSegments = baseSegments.map((segment, index) => {
      const segmentData = lineData[index] || {};
      const rotation = segmentData.rotation || 0;

      // Calculate the center point of the segment
      const centerX = (segment.start.x + segment.end.x) / 2;
      const centerY = (segment.start.y + segment.end.y) / 2;

      let rotatedStart = segment.start;
      let rotatedEnd = segment.end;

      // Apply rotation if there is any
      if (rotation !== 0) {
        const cos = Math.cos(rotation);
        const sin = Math.sin(rotation);

        // Rotate start point around center
        const startRelX = segment.start.x - centerX;
        const startRelY = segment.start.y - centerY;
        rotatedStart = {
          x: centerX + startRelX * cos - startRelY * sin,
          y: centerY + startRelX * sin + startRelY * cos
        };

        // Rotate end point around center
        const endRelX = segment.end.x - centerX;
        const endRelY = segment.end.y - centerY;
        rotatedEnd = {
          x: centerX + endRelX * cos - endRelY * sin,
          y: centerY + endRelX * sin + endRelY * cos
        };
      }

      return {
        start: rotatedStart,
        end: rotatedEnd,
        originalIndex: segment.originalIndex,
        originalStart: segment.start,
        originalEnd: segment.end,
        side: segment.side,
        segment: segment.segment
      };
    });

    // Second pass: adjust adjacent lines to maintain closure
    for (let i = 0; i < calculatedSegments.length; i++) {
      const currentSegment = calculatedSegments[i];
      const nextIndex = (i + 1) % calculatedSegments.length;
      const nextSegment = calculatedSegments[nextIndex];
      
      // If current segment's end point has moved, adjust next segment's start point
      if (currentSegment.end.x !== currentSegment.originalEnd.x || 
          currentSegment.end.y !== currentSegment.originalEnd.y) {
        nextSegment.start = currentSegment.end;
      }
      
      // If current segment's start point has moved, adjust previous segment's end point
      if (currentSegment.start.x !== currentSegment.originalStart.x || 
          currentSegment.start.y !== currentSegment.originalStart.y) {
        const prevIndex = (i - 1 + calculatedSegments.length) % calculatedSegments.length;
        calculatedSegments[prevIndex].end = currentSegment.start;
      }
    }

    // Convert to final format
    rotatedSegments.push(...calculatedSegments.map(segment => ({
      start: segment.start,
      end: segment.end,
      originalIndex: segment.originalIndex,
      side: segment.side,
      segment: segment.segment
    })));

    // Generate the final line shapes
    const lines = rotatedSegments.map((segment, index) => {
      const segmentData = lineData[index] || {};
      
      return {
        id: `${shape.id}-line-${index}`,
        type: "line",
        x: segment.start.x,
        y: segment.start.y,
        nodes: [
          { x: 0, y: 0 },
          { x: segment.end.x - segment.start.x, y: segment.end.y - segment.start.y }
        ],
        originalIndex: segment.originalIndex,
        side: segment.side,
        segment: segment.segment,
        strokeWidth: segmentData.strokeWidth || 1,
        ...(segmentData.isCurveMode ? {
          isCurveMode: segmentData.isCurveMode,
          controlPoint: segmentData.controlPoint
        } : {})
      };
    });

    // Filter out removed lines
    const removedLines = shape.removedLines || [];
    const hiddenBySharedLines = shape.hiddenBySharedLines || [];
    const filteredLines = lines.filter(
      (line) =>
      !removedLines.includes(line.originalIndex) && 
      !hiddenBySharedLines.includes(line.originalIndex)
    );

    return filteredLines;
  }, [shape, isPartOfMergedStructure, splitSides]);

  const getLineSegmentInfo = useCallback((lineIndex) => {
    // Generate current lines to get their side and segment info
    const lines = generateRectangleLines();
    if (lineIndex >= 0 && lineIndex < lines.length) {
      const line = lines[lineIndex];
      return {
        side: line.side,
        segment: line.segment
      };
    }
    return null;
  }, [generateRectangleLines]);

  // Handle line updates - when a line is modified, update the rectangle accordingly
  const handleLineUpdate = useCallback(
    (lineIndex, updates) => {
    if (!shape) return;

    // Handle thickness updates
    if (updates.strokeWidth !== undefined) {
      // Initialize lineData array if it doesn't exist
      const currentLineData = shape.lineData || new Array(8).fill({});
      const newLineData = [...currentLineData];
      
      // Update thickness for the specific line
      newLineData[lineIndex] = {
        ...newLineData[lineIndex],
          strokeWidth: updates.strokeWidth,
      };

      // If applyToAll is true, update all lines
      if (updates.applyToAll) {
        for (let i = 0; i < newLineData.length; i++) {
          newLineData[i] = {
            ...newLineData[i],
              strokeWidth: updates.strokeWidth,
          };
        }
      }

      // Update the shape with new line data
      onUpdate({
          lineData: newLineData,
      });
    }

    // If nodes are being updated (scaling), adjust only the affected segments
    if (updates.nodes) {
      const lines = generateRectangleLines();
        const currentLine = lines.find(
          (line) => line.originalIndex === lineIndex
        );
      
      if (!currentLine) return;

      const originalNodes = currentLine.nodes;
      const newNodes = updates.nodes;
      
      const sideInfo = getLineSegmentInfo(lineIndex);
      if (!sideInfo) return;

      // Calculate how much the line has changed
      const originalLength = Math.sqrt(
        Math.pow(originalNodes[1].x - originalNodes[0].x, 2) +
        Math.pow(originalNodes[1].y - originalNodes[0].y, 2)
      );
      
      const newLength = Math.sqrt(
        Math.pow(newNodes[1].x - newNodes[0].x, 2) +
        Math.pow(newNodes[1].y - newNodes[0].y, 2)
      );

      const lengthDelta = newLength - originalLength;

      // Don't update rectangle dimensions - instead, store the individual line scaling
      // This allows each line segment to have its own length while keeping the rectangle structure
      
      // Initialize lineScaling array if it doesn't exist
      const currentLineScaling = shape.lineScaling || new Array(8).fill(0);
      const newLineScaling = [...currentLineScaling];
      
      // Store the length delta for this specific line segment
        newLineScaling[lineIndex] =
          (currentLineScaling[lineIndex] || 0) + lengthDelta;
      
      // For the same side, adjust the neighboring segment to compensate
      // This keeps the total side length constant
      let neighborIndex = null;
        if (sideInfo.side === "top") {
        neighborIndex = lineIndex === 0 ? 1 : 0;
        } else if (sideInfo.side === "right") {
        neighborIndex = lineIndex === 2 ? 3 : 2;
        } else if (sideInfo.side === "bottom") {
        neighborIndex = lineIndex === 4 ? 5 : 4;
        } else if (sideInfo.side === "left") {
        neighborIndex = lineIndex === 6 ? 7 : 6;
      }

      if (neighborIndex !== null) {
          newLineScaling[neighborIndex] =
            (currentLineScaling[neighborIndex] || 0) - lengthDelta;
      }

      // Update the shape with new line scaling
      onUpdate({
          lineScaling: newLineScaling,
      });
    }

    // Handle curve updates
      if (
        updates.isCurveMode !== undefined ||
        updates.controlPoint !== undefined
      ) {
      // Initialize lineData array if it doesn't exist
      const currentLineData = shape.lineData || new Array(8).fill({});
      const newLineData = [...currentLineData];
      
      // Update curve data for the specific line
      newLineData[lineIndex] = {
        ...newLineData[lineIndex],
        isCurveMode: updates.isCurveMode,
          controlPoint: updates.controlPoint,
      };

      // Update the shape with new line data
      onUpdate({
          lineData: newLineData,
      });
    }
    },
    [shape, onUpdate, generateRectangleLines, getLineSegmentInfo]
  );

  // Handle line selection
  const handleLineSelect = useCallback(
    (lineIndex) => {
    setSelectedLineIndex(lineIndex);
    onSelect(); // Also select the rectangle
    
    // Ensure lineData exists before accessing it
    if (shape.lineData && shape.lineData[lineIndex]) {
      // Dispatch an event with the selected line information
      window.dispatchEvent(
        new CustomEvent("lineSelected", {
          detail: {
            rectangleId: shape.id,
            lineIndex,
              lineData: shape.lineData[lineIndex],
            },
        })
      );
    } else {
      // If lineData doesn't exist, initialize it
      const newLineData = new Array(8).fill({});
      newLineData[lineIndex] = {
        id: `${shape.id}-line-${lineIndex}`,
          type: "line",
        strokeWidth: 2, // default thickness
          isCurveMode: false,
      };
      
      // Update the shape with the new lineData
      onUpdate({
        id: shape.id,
          lineData: newLineData,
      });

      // Dispatch the event with the new line data
      window.dispatchEvent(
        new CustomEvent("lineSelected", {
          detail: {
            rectangleId: shape.id,
            lineIndex,
              lineData: newLineData[lineIndex],
            },
        })
      );
    }
    },
    [onSelect, shape.id, shape.lineData, onUpdate]
  );

  // Clean up selection when rectangle is deselected
  useEffect(() => {
    if (!isSelected) {
      setSelectedLineIndex(null);
      // Clear the selected line in FloorPlanner
      window.dispatchEvent(
        new CustomEvent("lineSelected", {
          detail: null,
        })
      );
    }
  }, [isSelected]);

 // Handle line removal
  const handleLineRemoval = useCallback(
    (lineIndex) => {
      if (!shape || isPartOfMergedStructure) {
    return;
  }

  // Initialize removedLines array if it doesn't exist
  const currentRemovedLines = shape.removedLines || [];
  const newRemovedLines = [...currentRemovedLines];
  
  // Add the line index to removed lines if not already present
  if (!newRemovedLines.includes(lineIndex)) {
    newRemovedLines.push(lineIndex);
  }

  // Clear any curve data for the removed line
  const currentLineData = shape.lineData || new Array(8).fill({});
  const newLineData = [...currentLineData];
  newLineData[lineIndex] = {}; // Clear the line data

  // Update the rectangle with removed lines and cleared line data
  onUpdate({
    id: shape.id,
    removedLines: newRemovedLines,
        lineData: newLineData,
  });

  // Deselect the line and reset deletion flag after a small delay
  setTimeout(() => {
    setSelectedLineIndex(null);
    isDeletingLine.current = false;
  }, 100);
    },
    [shape, onUpdate, isPartOfMergedStructure, selectedLineIndex]
  );

  // Handle keyboard events for line deletion
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle if this rectangle is selected
      if (!isSelected) {
        return;
      }
      
      // Handle modifier mode toggle
      if (event.key === "m" || event.key === "M") {
        event.preventDefault();
        event.stopPropagation();
        setIsModifierMode((prev) => !prev);
        // Exit modifier mode if a line is selected
        if (selectedLineIndex !== null) {
          setSelectedLineIndex(null);
        }
        return;
      }

      // Only handle line deletion if a line is selected
      if (selectedLineIndex === null) {
        return;
      }

      if (event.key === "Delete" || event.key === "Backspace") {
        // Stop the event from propagating to other handlers (like Sidebar's global delete)
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation(); // Prevent other listeners on the same element
        
        handleLineRemoval(selectedLineIndex);
      }

      // Handle 'S' key for splitting sides
      if (event.key === "s" || event.key === "S") {
        event.preventDefault();
        event.stopPropagation();
        
        // Get the side of the selected line
        const sideInfo = getLineSegmentInfo(selectedLineIndex);
        if (sideInfo) {
          const sideToSplit = sideInfo.side;
          
          // Only split if the side is not already split
          if (!splitSides[sideToSplit]) {
            setSplitSides(prev => ({
              ...prev,
              [sideToSplit]: true
            }));
            
            // Update the shape to persist splitSides state
            onUpdate({
              id: shape.id,
              splitSides: {
                ...splitSides,
                [sideToSplit]: true
              }
            });
            
            // Clear the selected line since indices will change
            setSelectedLineIndex(null);
          }
        }
      }
    };

    // Add event listener when rectangle is selected
    // Use capture phase to ensure we get the event before other handlers
    if (isSelected) {
      document.addEventListener("keydown", handleKeyDown, true); // true = capture phase
      return () => {
        document.removeEventListener("keydown", handleKeyDown, true);
      };
    }
  }, [
    isSelected,
    selectedLineIndex,
    handleLineRemoval,
    shape.id,
    isMergedStructureShape,
    mergedStructureId,
    getLineSegmentInfo,
    splitSides,
    onUpdate,
  ]);

  // Handle name editing
  const handleNameClick = useCallback(
    (event) => {
    if (!isSelected) return;
    
    event.stopPropagation();
    
    // Dispatch event to show the name editing modal
      window.dispatchEvent(
        new CustomEvent("showRectangleNameModal", {
      detail: {
        rectangleId: shape.id,
            currentName: shape.name || "Room",
          },
        })
      );
    },
    [isSelected, shape.id, shape.name]
  );

  // Helper functions for flooring patterns with curves
  const calculateRoomBounds = useCallback((lines, shape) => {
    // Calculate the actual room boundaries considering curves
    const bounds = {
      minX: shape.x,
      minY: shape.y,
      maxX: shape.x + shape.width,
      maxY: shape.y + shape.height,
      curves: [],
    };

    // Check for curved lines and adjust bounds
    lines.forEach((line, index) => {
      if (
        shape.lineData &&
        shape.lineData[index] &&
        shape.lineData[index].isCurveMode
      ) {
        const controlPoint = shape.lineData[index].controlPoint;
        if (controlPoint) {
          // Adjust bounds to include curve control points
          bounds.minX = Math.min(bounds.minX, controlPoint.x);
          bounds.minY = Math.min(bounds.minY, controlPoint.y);
          bounds.maxX = Math.max(bounds.maxX, controlPoint.x);
          bounds.maxY = Math.max(bounds.maxY, controlPoint.y);
          
          bounds.curves.push({
            lineIndex: index,
            controlPoint: controlPoint,
            startPoint: { x: line.x, y: line.y },
            endPoint: {
              x: line.x + line.nodes[1].x,
              y: line.y + line.nodes[1].y,
            },
          });
        }
      }
    });

    return bounds;
  }, []);

  const applyStandardPattern = useCallback((g, patternType, shape, angle = 0) => {
    const angleRad = (angle * Math.PI) / 180; // Convert to radians
    const centerX = shape.x + shape.width / 2;
    const centerY = shape.y + shape.height / 2;
    
    // Calculate diagonal length to ensure pattern covers entire area when rotated
    const diagonal = Math.sqrt(shape.width * shape.width + shape.height * shape.height);
    const extension = diagonal; // Extend patterns by the diagonal length
    
    // Helper function to rotate a point around the center
    const rotatePoint = (x, y, angle) => {
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const dx = x - centerX;
      const dy = y - centerY;
      return {
        x: centerX + dx * cos - dy * sin,
        y: centerY + dx * sin + dy * cos
      };
    };

    switch (patternType) {
      case "oak":
      case "maple":
      case "walnut":
      case "cherry":
        // Wood grain pattern with angle support - extend beyond rectangle
        for (let i = -extension; i <= shape.width + extension; i += 4) {
          const startPoint = rotatePoint(shape.x + i, shape.y - extension, angleRad);
          const endPoint = rotatePoint(shape.x + i, shape.y + shape.height + extension, angleRad);
          g.moveTo(startPoint.x, startPoint.y);
          g.lineTo(endPoint.x, endPoint.y);
        }
        break;
      case "ceramic":
      case "marble":
      case "granite":
        // Tile pattern with angle support - extend beyond rectangle
        for (let i = -extension; i <= shape.width + extension; i += 8) {
          const startPoint = rotatePoint(shape.x + i, shape.y - extension, angleRad);
          const endPoint = rotatePoint(shape.x + i, shape.y + shape.height + extension, angleRad);
          g.moveTo(startPoint.x, startPoint.y);
          g.lineTo(endPoint.x, endPoint.y);
        }
        for (let i = -extension; i <= shape.height + extension; i += 8) {
          const startPoint = rotatePoint(shape.x - extension, shape.y + i, angleRad);
          const endPoint = rotatePoint(shape.x + shape.width + extension, shape.y + i, angleRad);
          g.moveTo(startPoint.x, startPoint.y);
          g.lineTo(endPoint.x, endPoint.y);
        }
        break;
      case "mosaic":
        // Mosaic pattern - extend beyond rectangle for complete coverage
        for (let i = -extension; i <= shape.width + extension; i += 6) {
          for (let j = -extension; j <= shape.height + extension; j += 6) {
            const point = rotatePoint(shape.x + i, shape.y + j, angleRad);
            g.drawCircle(point.x, point.y, 1);
          }
        }
        break;
      case "textured":
        // Textured pattern with angle support - extend beyond rectangle
        for (let i = -extension; i <= shape.width + extension; i += 3) {
          const startPoint = rotatePoint(shape.x + i, shape.y - extension, angleRad);
          const endPoint = rotatePoint(shape.x + i + 2, shape.y + 2 - extension, angleRad);
          g.moveTo(startPoint.x, startPoint.y);
          g.lineTo(endPoint.x, endPoint.y);
        }
        break;
      case "patterned":
        // Patterned carpet - extend beyond rectangle for complete coverage
        for (let i = -extension; i <= shape.width + extension; i += 8) {
          for (let j = -extension; j <= shape.height + extension; j += 8) {
            const point = rotatePoint(shape.x + i, shape.y + j, angleRad);
            g.drawCircle(point.x, point.y, 2);
          }
        }
        break;
      case "concrete":
        // Concrete texture with angle support - extend beyond rectangle
        for (let i = -extension; i <= shape.width + extension; i += 2) {
          const startPoint = rotatePoint(shape.x + i, shape.y - extension, angleRad);
          const endPoint = rotatePoint(shape.x + i, shape.y + shape.height + extension, angleRad);
          g.moveTo(startPoint.x, startPoint.y);
          g.lineTo(endPoint.x, endPoint.y);
        }
        break;
      case "stone":
        // Stone texture - extend beyond rectangle for complete coverage
        for (let i = -extension; i <= shape.width + extension; i += 10) {
          for (let j = -extension; j <= shape.height + extension; j += 10) {
            const point = rotatePoint(shape.x + i, shape.y + j, angleRad);
            g.drawEllipse(point.x, point.y, 3, 2);
          }
        }
        break;
    }
  }, []);

  const applyPatternToRoomShape = useCallback(
    (g, patternType, shape, angle = 0) => {
    // Apply pattern that respects the actual room shape but stays within rectangle bounds
    const lines = generateRectangleLines();
      const hasCurves =
        shape.lineData && shape.lineData.some((line) => line.isCurveMode);
    
    if (hasCurves) {
      // For rooms with curves, apply pattern within the rectangle bounds only
        const patternSpacing = patternType.includes("wood")
          ? 4
          : patternType.includes("tile")
          ? 8
          : patternType.includes("mosaic")
          ? 6
          : patternType.includes("textured")
          ? 3
          : patternType.includes("patterned")
          ? 8
          : patternType.includes("concrete")
          ? 2
          : 10;
      
      // Apply pattern within the rectangle bounds only with angle support
      const angleRad = (angle * Math.PI) / 180; // Convert to radians
      const centerX = shape.x + shape.width / 2;
      const centerY = shape.y + shape.height / 2;
      
      // Calculate diagonal length to ensure pattern covers entire area when rotated
      const diagonal = Math.sqrt(shape.width * shape.width + shape.height * shape.height);
      const extension = diagonal; // Extend patterns by the diagonal length
      
      // Helper function to rotate a point around the center
      const rotatePoint = (x, y, angle) => {
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const dx = x - centerX;
        const dy = y - centerY;
        return {
          x: centerX + dx * cos - dy * sin,
          y: centerY + dx * sin + dy * cos
        };
      };
      
      // Generate extended pattern that covers the entire rectangle when rotated
      for (let x = shape.x - extension; x <= shape.x + shape.width + extension; x += patternSpacing) {
        for (let y = shape.y - extension; y <= shape.y + shape.height + extension; y += patternSpacing) {
          // Apply pattern element based on type
          switch (patternType) {
            case "mosaic":
            case "patterned":
              const point = rotatePoint(x, y, angleRad);
              g.drawCircle(point.x, point.y, patternType === "mosaic" ? 1 : 2);
              break;
            case "stone":
              const stonePoint = rotatePoint(x, y, angleRad);
              g.drawEllipse(stonePoint.x, stonePoint.y, 3, 2);
              break;
            default:
              // For line-based patterns, draw lines that extend beyond bounds
              if (
                patternType.includes("wood") ||
                patternType.includes("tile") ||
                patternType.includes("concrete")
              ) {
                const startPoint = rotatePoint(x, shape.y - extension, angleRad);
                const endPoint = rotatePoint(x, shape.y + shape.height + extension, angleRad);
                g.moveTo(startPoint.x, startPoint.y);
                g.lineTo(endPoint.x, endPoint.y);
              }
              break;
          }
        }
      }
      
      // For tile patterns, also draw horizontal lines with extended coverage
      if (patternType.includes("tile")) {
        for (let y = shape.y - extension; y <= shape.y + shape.height + extension; y += patternSpacing) {
          const startPoint = rotatePoint(shape.x - extension, y, angleRad);
          const endPoint = rotatePoint(shape.x + shape.width + extension, y, angleRad);
          g.moveTo(startPoint.x, startPoint.y);
          g.lineTo(endPoint.x, endPoint.y);
        }
      }
    } else {
      // No curves, use standard pattern
      applyStandardPattern(g, patternType, shape, angle);
    }
    },
    [generateRectangleLines, applyStandardPattern]
  );

  // Handle rotation handle mouse down
  const handleRotationHandleMouseDown = useCallback((event, lineIndex) => {
    if (!isModifierMode) return;
    
    event.stopPropagation();
    setRotatingLineIndex(lineIndex);
    
    // Get coordinates properly
    let globalPoint;
    if (event.data?.global) {
      globalPoint = event.data.global;
    } else if (event.clientX !== undefined) {
      globalPoint = getCoordinates(event.clientX, event.clientY);
      if (!globalPoint) return;
    } else {
      return;
    }
    
    setRotationStart({
      x: globalPoint.x,
      y: globalPoint.y,
      lineIndex: lineIndex
    });
  }, [isModifierMode, getCoordinates]);

  // Function to rotate a line while keeping rectangle closed
  const rotateLine = useCallback((lineIndex, targetRotation) => {
    if (!shape) return;

    // Get the current line data
    const currentLineData = shape.lineData || new Array(8).fill({});
    const lineData = currentLineData[lineIndex] || {};
    
    // Limit rotation to prevent extreme angles
    const maxRotation = Math.PI / 6; // 30 degrees
    const clampedRotation = Math.max(-maxRotation, Math.min(maxRotation, targetRotation));

    // Update line data with new rotation
    const newLineData = [...currentLineData];
    newLineData[lineIndex] = {
      ...lineData,
      rotation: clampedRotation
    };

    // Update the rectangle
    onUpdate({
      id: shape.id,
      lineData: newLineData
    });
  }, [shape, onUpdate]);

  // Handle rotation handle mouse move
  const handleRotationHandleMouseMove = useCallback((event) => {
    if (!isModifierMode || rotatingLineIndex === null || !rotationStart) return;

    // Get coordinates properly
    let globalPoint;
    if (event.data?.global) {
      globalPoint = event.data.global;
    } else if (event.clientX !== undefined) {
      globalPoint = getCoordinates(event.clientX, event.clientY);
      if (!globalPoint) return;
    } else {
      return;
    }

    // Get the base segments to calculate the line center
    const baseHalfWidth = shape.width / 2;
    const baseHalfHeight = shape.height / 2;
    const lineScaling = shape.lineScaling || new Array(8).fill(0);
    
    // Calculate segment lengths
    const topSegment1Length = baseHalfWidth + lineScaling[0];
    const topSegment2Length = baseHalfWidth + lineScaling[1];
    const rightSegment1Length = baseHalfHeight + lineScaling[2];
    const rightSegment2Length = baseHalfHeight + lineScaling[3];
    const bottomSegment1Length = baseHalfWidth + lineScaling[4];
    const bottomSegment2Length = baseHalfWidth + lineScaling[5];
    const leftSegment1Length = baseHalfHeight + lineScaling[6];
    const leftSegment2Length = baseHalfHeight + lineScaling[7];

    // Define base segments
    const baseSegments = [
      // Top segments
      {
        start: { x: shape.x, y: shape.y },
        end: { x: shape.x + topSegment1Length, y: shape.y },
        originalIndex: 0
      },
      {
        start: { x: shape.x + topSegment1Length, y: shape.y },
        end: { x: shape.x + shape.width, y: shape.y },
        originalIndex: 1
      },
      // Right segments
      {
        start: { x: shape.x + shape.width, y: shape.y },
        end: { x: shape.x + shape.width, y: shape.y + rightSegment1Length },
        originalIndex: 2
      },
      {
        start: { x: shape.x + shape.width, y: shape.y + rightSegment1Length },
        end: { x: shape.x + shape.width, y: shape.y + shape.height },
        originalIndex: 3
      },
      // Bottom segments
      {
        start: { x: shape.x + shape.width, y: shape.y + shape.height },
        end: { x: shape.x + shape.width - bottomSegment1Length, y: shape.y + shape.height },
        originalIndex: 4
      },
      {
        start: { x: shape.x + shape.width - bottomSegment1Length, y: shape.y + shape.height },
        end: { x: shape.x, y: shape.y + shape.height },
        originalIndex: 5
      },
      // Left segments
      {
        start: { x: shape.x, y: shape.y + shape.height },
        end: { x: shape.x, y: shape.y + shape.height - leftSegment1Length },
        originalIndex: 6
      },
      {
        start: { x: shape.x, y: shape.y + shape.height - leftSegment1Length },
        end: { x: shape.x, y: shape.y },
        originalIndex: 7
      }
    ];

    const segment = baseSegments[rotatingLineIndex];
    if (!segment) return;

    // Calculate the center point of the segment
    const centerX = (segment.start.x + segment.end.x) / 2;
    const centerY = (segment.start.y + segment.end.y) / 2;

    // Calculate the angle from center to mouse position
    const mouseAngle = Math.atan2(globalPoint.y - centerY, globalPoint.x - centerX);
    
    // Calculate the base angle of the line segment (original orientation)
    const baseLineAngle = Math.atan2(segment.end.y - segment.start.y, segment.end.x - segment.start.x);
    
    // Calculate the rotation angle (difference between mouse angle and base line angle)
    const rotationAngle = mouseAngle - baseLineAngle;

    // Apply rotation to the line
    rotateLine(rotatingLineIndex, rotationAngle);
  }, [isModifierMode, rotatingLineIndex, rotationStart, getCoordinates, shape, rotateLine]);

  // Handle rotation handle mouse up
  const handleRotationHandleMouseUp = useCallback(() => {
    setRotatingLineIndex(null);
    setRotationStart(null);
  }, []);

  // Add global mouse event listeners for rotation
  useEffect(() => {
    if (isModifierMode && rotatingLineIndex !== null) {
      const handleGlobalMouseMove = (event) => {
        if (!rotationStart) return;

        const globalPoint = getCoordinates(event.clientX, event.clientY);
        if (!globalPoint) return;

        // Get the base segments to calculate the line center
        const baseHalfWidth = shape.width / 2;
        const baseHalfHeight = shape.height / 2;
        const lineScaling = shape.lineScaling || new Array(8).fill(0);

        // Calculate segment lengths
        const topSegment1Length = baseHalfWidth + lineScaling[0];
        const topSegment2Length = baseHalfWidth + lineScaling[1];
        const rightSegment1Length = baseHalfHeight + lineScaling[2];
        const rightSegment2Length = baseHalfHeight + lineScaling[3];
        const bottomSegment1Length = baseHalfWidth + lineScaling[4];
        const bottomSegment2Length = baseHalfWidth + lineScaling[5];
        const leftSegment1Length = baseHalfHeight + lineScaling[6];
        const leftSegment2Length = baseHalfHeight + lineScaling[7];

        // Define base segments
        const baseSegments = [
          // Top segments
          {
            start: { x: shape.x, y: shape.y },
            end: { x: shape.x + topSegment1Length, y: shape.y },
            originalIndex: 0,
          },
          {
            start: { x: shape.x + topSegment1Length, y: shape.y },
            end: { x: shape.x + shape.width, y: shape.y },
            originalIndex: 1,
          },
          // Right segments
          {
            start: { x: shape.x + shape.width, y: shape.y },
            end: { x: shape.x + shape.width, y: shape.y + rightSegment1Length },
            originalIndex: 2,
          },
          {
            start: {
              x: shape.x + shape.width,
              y: shape.y + rightSegment1Length,
            },
            end: { x: shape.x + shape.width, y: shape.y + shape.height },
            originalIndex: 3,
          },
          // Bottom segments
          {
            start: { x: shape.x + shape.width, y: shape.y + shape.height },
            end: {
              x: shape.x + shape.width - bottomSegment1Length,
              y: shape.y + shape.height,
            },
            originalIndex: 4,
          },
          {
            start: {
              x: shape.x + shape.width - bottomSegment1Length,
              y: shape.y + shape.height,
            },
            end: { x: shape.x, y: shape.y + shape.height },
            originalIndex: 5,
          },
          // Left segments
          {
            start: { x: shape.x, y: shape.y + shape.height },
            end: { x: shape.x, y: shape.y + shape.height - leftSegment1Length },
            originalIndex: 6,
          },
          {
            start: {
              x: shape.x,
              y: shape.y + shape.height - leftSegment1Length,
            },
            end: { x: shape.x, y: shape.y },
            originalIndex: 7,
          },
        ];

        const segment = baseSegments[rotatingLineIndex];
        if (!segment) return;

        // Calculate the center point of the segment
        const centerX = (segment.start.x + segment.end.x) / 2;
        const centerY = (segment.start.y + segment.end.y) / 2;

        // Calculate the angle from center to mouse position
        const mouseAngle = Math.atan2(globalPoint.y - centerY, globalPoint.x - centerX);
        
        // Calculate the base angle of the line segment (original orientation)
        const baseLineAngle = Math.atan2(segment.end.y - segment.start.y, segment.end.x - segment.start.x);
        
        // Calculate the rotation angle (difference between mouse angle and base line angle)
        const rotationAngle = mouseAngle - baseLineAngle;

        // Apply rotation to the line
        rotateLine(rotatingLineIndex, rotationAngle);
      };

      const handleGlobalMouseUp = () => {
        setRotatingLineIndex(null);
        setRotationStart(null);
      };

      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }
  }, [
    isModifierMode,
    rotatingLineIndex,
    rotationStart,
    getCoordinates,
    shape,
    rotateLine,
  ]);

  if (!shape) return null;

  return (
    <>
      <Container
        ref={containerRef}
        x={0}
        y={0}
        interactive={true}
        pointerdown={handleMouseDown}
        sortableChildren={true}
      >
        {/* Invisible hit area for rectangle dragging */}
        <Graphics
          cursor={"pointer"}
          draw={(g) => {
            g.clear();
            g.lineStyle(0);
            // Use shape alpha for preview mode, default to very low alpha for hit detection
            const alpha = shape.isPreview ? shape.alpha || 0.5 : 0.01;
            g.beginFill(0x000000, alpha);
            g.drawRect(shape.x, shape.y, shape.width, shape.height);
            g.endFill();
          }}
          interactive={true}
          pointerdown={handleMouseDown}
          click={handleMouseDown}
          mousedown={handleMouseDown}
          pointermove={isDragging ? handleMouseMove : undefined}
          pointerup={handleMouseUp}
        />

        {/* Flooring pattern background */}
        {shape.flooringPattern && (
          <Container>
            {/* Base color fill */}
            <Graphics
              draw={(g) => {
                g.clear();
                g.lineStyle(0);
                
                // Set the base color
                const baseColor = shape.flooringPattern.color;
                g.beginFill(baseColor, 0.3); // Semi-transparent
                
                // Always draw the base rectangle fill first
                g.drawRect(shape.x, shape.y, shape.width, shape.height);
                g.endFill();
              }}
              interactive={false}
            />
            
            {/* Pattern overlay with clipping */}
            {shape.flooringPattern.pattern && (
              <Container>
                {/* Create mask for clipping */}
                <Graphics
                  draw={(g) => {
                    g.clear();
                    g.beginFill(0xffffff, 1);
                    g.drawRect(shape.x, shape.y, shape.width, shape.height);
                    g.endFill();
                  }}
                  ref={(maskRef) => {
                    // Apply mask to the pattern container
                    if (maskRef && maskRef.parent) {
                      maskRef.parent.mask = maskRef;
                    }
                  }}
                />
                
                {/* Pattern graphics */}
                <Graphics
                  draw={(g) => {
                    g.clear();
                    g.lineStyle(1, 0x000000, 0.1);
                    // Use the angle from the pattern object, default to 0 if not set
                    const patternAngle = shape.flooringPattern.angle || 0;
                    applyStandardPattern(g, shape.flooringPattern.pattern, shape, patternAngle);
                  }}
                  interactive={false}
                />
              </Container>
            )}
          </Container>
        )}

        {/* Highlight overlay */}
        {shape.highlightColor && (
          <Graphics
            draw={(g) => {
              g.clear();
              g.lineStyle(0);
              g.beginFill(shape.highlightColor.hex, 0.2); // Semi-transparent highlight
              g.drawRect(shape.x, shape.y, shape.width, shape.height);
              g.endFill();
            }}
            interactive={false} // Prevent highlight from interfering with interactions
          />
        )}

        {/* Render each side as an EditableLine */}
        {generateRectangleLines().map((lineShape, index) => {
          const originalIndex = lineShape.originalIndex;
          return (
            <EditableLine
              key={lineShape.id}
              shape={lineShape}
              isSelected={isSelected && selectedLineIndex === originalIndex}
              onUpdate={(updates) => handleLineUpdate(originalIndex, updates)}
              onSelect={() => handleLineSelect(originalIndex)}
              stageRef={stageRef}
              isPartOfRectangle={true}
              rectangleIndex={originalIndex}
              onRectangleNodeMouseDown={handleNodeMouseDown}
              selectedMeasurementUnit={selectedMeasurementUnit}
              isBlueprintMode={isBlueprintMode}
            />
          );
        })}

        {/* Rotation handles for modifier mode */}
        {isSelected && isModifierMode && (
          <>
            {generateRectangleLines().map((lineShape, index) => {
              const originalIndex = lineShape.originalIndex;
              const lineCenter = {
                x: lineShape.x + lineShape.nodes[1].x / 2,
                y: lineShape.y + lineShape.nodes[1].y / 2,
              };

              const isRotating = rotatingLineIndex === originalIndex;

              return (
                <Graphics
                  key={`rotation-handle-${originalIndex}`}
                  draw={(g) => {
                    g.clear();
                    // Different colors for active vs inactive handles
                    const borderColor = isRotating ? 0xff0000 : 0x00ff00; // Red when rotating, green otherwise
                    const fillColor = isRotating ? 0xffcccc : 0xffffff; // Light red when rotating, white otherwise

                    g.lineStyle(2, borderColor, 1);
                    g.beginFill(fillColor, 0.9);
                    g.drawCircle(0, 0, 8); // 8px radius circle
                    g.endFill();

                    // Add rotation indicator
                    g.lineStyle(1, borderColor, 1);
                    g.moveTo(0, -12);
                    g.lineTo(0, -8);
                    g.moveTo(-3, -10);
                    g.lineTo(3, -10);

                    // Add rotation arrows
                    g.moveTo(-6, 0);
                    g.lineTo(-2, 0);
                    g.moveTo(-4, -2);
                    g.lineTo(-2, 0);
                    g.moveTo(-4, 2);
                    g.lineTo(-2, 0);

                    g.moveTo(6, 0);
                    g.lineTo(2, 0);
                    g.moveTo(4, -2);
                    g.lineTo(2, 0);
                    g.moveTo(4, 2);
                    g.lineTo(2, 0);
                  }}
                  x={lineCenter.x}
                  y={lineCenter.y}
                  interactive={true}
                  cursor={isRotating ? "grabbing" : "grab"}
                  pointerdown={(event) =>
                    handleRotationHandleMouseDown(event, originalIndex)
                  }
                  pointermove={handleRotationHandleMouseMove}
                  pointerup={handleRotationHandleMouseUp}
                />
              );
            })}
          </>
        )}

        {/* Render editable name */}
        {(shape.name || isSelected) && (
          <Text
            text={shape.name || "Room"}
            x={shape.x + shape.width / 2}
            y={shape.y + shape.height / 2}
            anchor={0.5}
            style={{
              fontFamily: "Arial",
              fontSize: Math.min(shape.width / 6, shape.height / 3, 16),
              fill: isBlueprintMode ? 0xffffff : 0x000000, // White text in blueprint mode, black otherwise
              align: "center",
              fontWeight: "bold",
              dropShadow: true, // Always show shadow for visibility
              dropShadowColor: 0xffffff,
              dropShadowBlur: 2,
              dropShadowAngle: Math.PI / 6,
              dropShadowDistance: 1,
            }}
            interactive={isSelected}
            pointerdown={handleNameClick}
            cursor={isSelected ? "pointer" : "default"}
          />
        )}

        {/* Modifier mode indicator */}
        {isSelected && isModifierMode && (
          <Text
            text="MODIFIER MODE - Press M to exit"
            x={shape.x + shape.width / 2}
            y={shape.y - 30}
            anchor={0.5}
            style={{
              fontFamily: "Arial",
              fontSize: 12,
              fill: 0x00ff00, // Green text
              align: "center",
              fontWeight: "bold",
              dropShadow: true,
              dropShadowColor: 0x000000,
              dropShadowBlur: 2,
              dropShadowDistance: 1,
            }}
          />
        )}

        {/* Rotation angle indicator */}
        {isSelected && isModifierMode && rotatingLineIndex !== null && (
          <Text
            text={`Rotating Line ${rotatingLineIndex + 1}`}
            x={shape.x + shape.width / 2}
            y={shape.y - 50}
            anchor={0.5}
            style={{
              fontFamily: "Arial",
              fontSize: 10,
              fill: 0xff0000, // Red text
              align: "center",
              fontWeight: "bold",
              dropShadow: true,
              dropShadowColor: 0xffffff,
              dropShadowBlur: 1,
              dropShadowDistance: 1,
            }}
          />
        )}

        {/* Square footage display */}
        {shape.showSquareFootage && (
          <Text
            text={`${((shape.width * shape.height) / 10000).toFixed(1)} sq ft`}
            x={shape.x + shape.width / 2}
            y={shape.y + shape.height / 2 + 20}
            anchor={0.5}
            style={{
              fontFamily: "Arial",
              fontSize: Math.min(shape.width / 8, shape.height / 4, 12),
              fill: 0x000000,
              align: "center",
              fontWeight: "bold",
              dropShadow: true,
              dropShadowColor: 0xffffff,
              dropShadowBlur: 2,
              dropShadowAngle: Math.PI / 6,
              dropShadowDistance: 1,
            }}
          />
        )}

        {/* Inside dimensions */}
        {shape.showInsideDimensions && (
          <>
            <Text
              text={`${((shape.width / 100) * (selectedMeasurementUnit?.conversionFactor || 3.28084)).toFixed(1)}${selectedMeasurementUnit?.abbreviation || "'"}`}
              x={shape.x + shape.width / 2}
              y={shape.y + 10}
              anchor={0.5}
              style={{
                fontFamily: "Arial",
                fontSize: 10,
                fill: 0x000000,
                align: "center",
                fontWeight: "bold",
                dropShadow: true,
                dropShadowColor: 0xffffff,
                dropShadowBlur: 1,
                dropShadowDistance: 1,
              }}
            />
            <Text
              text={`${((shape.height / 100) * (selectedMeasurementUnit?.conversionFactor || 3.28084)).toFixed(1)}${selectedMeasurementUnit?.abbreviation || "'"}`}
              x={shape.x + shape.width - 10}
              y={shape.y + shape.height / 2}
              anchor={0.5}
              style={{
                fontFamily: "Arial",
                fontSize: 10,
                fill: 0x000000,
                align: "center",
                fontWeight: "bold",
                dropShadow: true,
                dropShadowColor: 0xffffff,
                dropShadowBlur: 1,
                dropShadowDistance: 1,
              }}
            />
          </>
        )}

        {/* Outside dimensions */}
        {shape.showOutsideDimensions && (
          <>
            <Text
              text={`${((shape.width / 100) * (selectedMeasurementUnit?.conversionFactor || 3.28084)).toFixed(1)}${selectedMeasurementUnit?.abbreviation || "'"}`}
              x={shape.x + shape.width / 2}
              y={shape.y - 10}
              anchor={0.5}
              style={{
                fontFamily: "Arial",
                fontSize: 10,
                fill: 0x666666,
                align: "center",
                fontWeight: "bold",
                dropShadow: true,
                dropShadowColor: 0xffffff,
                dropShadowBlur: 1,
                dropShadowDistance: 1,
              }}
            />
            <Text
              text={`${((shape.height / 100) * (selectedMeasurementUnit?.conversionFactor || 3.28084)).toFixed(1)}${selectedMeasurementUnit?.abbreviation || "'"}`}
              x={shape.x + shape.width + 20}
              y={shape.y + shape.height / 2}
              anchor={0.5}
              style={{
                fontFamily: "Arial",
                fontSize: 10,
                fill: 0x666666,
                align: "center",
                fontWeight: "bold",
                dropShadow: true,
                dropShadowColor: 0xffffff,
                dropShadowBlur: 1,
                dropShadowDistance: 1,
              }}
            />
          </>
        )}

        {/* Corner nodes for rectangle scaling - positioned relative to rectangle */}
        {isSelected && !isPartOfMergedStructure && (
          <>
            {/* Corner nodes */}
            {[
              { x: shape.x, y: shape.y }, // top-left
              { x: shape.x + shape.width, y: shape.y }, // top-right
              { x: shape.x + shape.width, y: shape.y + shape.height }, // bottom-right
              { x: shape.x, y: shape.y + shape.height }, // bottom-left
            ].map((pos, index) => (
              <CornerNode
                key={`corner-${index}`}
                x={pos.x}
                y={pos.y}
                index={index}
                shape={shape}
                onNodeMouseDown={handleNodeMouseDown}
                onNodeMouseMove={handleNodeMouseMove}
                onNodeMouseUp={handleNodeMouseUp}
                containerRef={containerRef}
              />
            ))}
          </>
        )}
      </Container>
    </>
  );
};

export default Rectangle;
